import type React from "react";
import { useState } from "react";
import styled from "styled-components";
import { colours, pxToRem } from "../../helpers";

interface DfsvFormProps {
	title?: string;
}

const FormContainer = styled.div`
	max-width: ${pxToRem(800)};
	margin: 0 auto;
	padding: ${pxToRem(40)} ${pxToRem(20)};
`;

const IntroText = styled.p`
	font-size: ${pxToRem(16)};
	line-height: 1.6;
	color: ${colours.MIDNIGHT_BLUE};
	font-family: var(--euclid-regular);
	margin-bottom: ${pxToRem(24)};
`;

const StyledForm = styled.form`
	display: flex;
	flex-direction: column;
	gap: ${pxToRem(24)};
`;

const FormField = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${pxToRem(8)};
`;

const Label = styled.label<{ required?: boolean }>`
	font-size: ${pxToRem(16)};
	font-weight: 500;
	color: ${colours.MIDNIGHT_BLUE};
	font-family: var(--euclid-medium);
	
	${({ required }) =>
		required &&
		`
		&::after {
			content: " *";
			color: ${colours.ERROR_RED};
		}
	`}
`;

const Input = styled.input<{ hasError?: boolean }>`
	height: ${pxToRem(40)};
	padding: 0 ${pxToRem(16)};
	font-size: ${pxToRem(16)};
	border: 1px solid ${({ hasError }) => (hasError ? colours.ERROR_RED : colours.LIGHT_GREY)};
	border-radius: ${pxToRem(4)};
	font-family: var(--euclid-regular);
	color: ${colours.MIDNIGHT_BLUE};
	
	&:focus {
		outline: none;
		border-color: ${colours.MIDNIGHT_BLUE};
	}
	
	&::placeholder {
		color: rgba(0, 0, 0, 0.4);
		font-family: var(--euclid-regular);
		font-size: ${pxToRem(16)};
	}
`;

const Select = styled.select<{ hasError?: boolean }>`
	height: ${pxToRem(40)};
	padding: 0 ${pxToRem(16)};
	font-size: ${pxToRem(16)};
	border: 1px solid ${({ hasError }) => (hasError ? colours.ERROR_RED : colours.LIGHT_GREY)};
	border-radius: ${pxToRem(4)};
	font-family: var(--euclid-regular);
	color: ${colours.MIDNIGHT_BLUE};
	background-color: white;
	cursor: pointer;
	
	&:focus {
		outline: none;
		border-color: ${colours.MIDNIGHT_BLUE};
	}
`;

const TextArea = styled.textarea<{ hasError?: boolean }>`
	padding: ${pxToRem(12)} ${pxToRem(16)};
	font-size: ${pxToRem(16)};
	border: 1px solid ${({ hasError }) => (hasError ? colours.ERROR_RED : colours.LIGHT_GREY)};
	border-radius: ${pxToRem(4)};
	font-family: var(--euclid-regular);
	color: ${colours.MIDNIGHT_BLUE};
	min-height: ${pxToRem(100)};
	resize: vertical;
	
	&:focus {
		outline: none;
		border-color: ${colours.MIDNIGHT_BLUE};
	}
	
	&::placeholder {
		color: rgba(0, 0, 0, 0.4);
		font-family: var(--euclid-regular);
		font-size: ${pxToRem(16)};
	}
`;

const ErrorMessage = styled.span`
	color: #D21919;
	font-family: var(--euclid-regular);
	font-size: 15px;
	line-height: 1.25em;
	font-weight: 400 !important;
	display: block;
	margin-top: 0.25rem;
`;

const CheckboxInput = styled.input`
	position: absolute;
	opacity: 0;
	height: 0;
	width: 0;
	cursor: pointer;
`;

const CheckboxBox = styled.div<{ checked: boolean }>`
	position: relative;
	flex-shrink: 0;
	height: ${pxToRem(21)};
	width: ${pxToRem(21)};
	background-color: ${({ checked }) => (checked ? colours.MIDNIGHT_BLUE : colours.WHITE)};
	border-radius: ${pxToRem(5)};
	border: 1px solid ${colours.MIDNIGHT_BLUE};
	transition: background-color 150ms ease-out;
	display: flex;
	justify-content: center;
	align-items: center;
	
	&::after {
		content: "";
		display: ${({ checked }) => (checked ? "block" : "none")};
		width: ${pxToRem(6)};
		height: ${pxToRem(10)};
		border: solid white;
		border-width: 0 2px 2px 0;
		transform: rotate(45deg);
		margin-bottom: ${pxToRem(2)};
	}
`;

const CheckboxLabel = styled.label<{ hasError?: boolean }>`
	display: flex;
	align-items: flex-start;
	gap: ${pxToRem(12)};
	font-size: ${pxToRem(16)};
	color: ${colours.MIDNIGHT_BLUE};
	font-family: var(--euclid-regular);
	cursor: pointer;
	padding: ${pxToRem(12)};
	border-radius: ${pxToRem(4)};
	border: 1px solid ${({ hasError }) => (hasError ? colours.ERROR_RED : "transparent")};
	background-color: ${({ hasError }) => (hasError ? "rgba(255, 0, 0, 0.05)" : "transparent")};
	
	a {
		color: ${colours.FELIX_BLUE};
		text-decoration: underline;
		font-family: var(--euclid-medium);
	}
`;

const SubmitButton = styled.button`
	padding: ${pxToRem(16)} ${pxToRem(32)};
	font-size: ${pxToRem(16)};
	font-weight: 600;
	font-family: var(--euclid-medium);
	color: white;
	background-color: ${colours.MIDNIGHT_BLUE};
	border: 2px solid ${colours.MIDNIGHT_BLUE};
	border-radius: 12px;
	cursor: pointer;
	margin-top: ${pxToRem(16)};
	transform: none !important;
	transition: none !important;
	
	&:hover {
		transform: none !important;
		/* border: none !important; */
		border: 2px solid ${colours.MIDNIGHT_BLUE} !important;

		/* background-color: ${colours.MIDNIGHT_BLUE}; */
	}
	
	&:disabled {
		background-color: #ccc;
		color: #888;
		cursor: not-allowed;
		opacity: 0.6;
	}
`;

const DfsvForm: React.FC<DfsvFormProps> = () => {
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [formData, setFormData] = useState({
		firstName: "",
		lastName: "",
		accountNumber: "",
		contactMethod: "",
		contactMsisdn: "",
		notificationEmail: "",
		callBackTime1: "",
		preferredContactTime: "",
		callBackTime2: "",
		howCanWeAssist: "",
		safeToLeaveMessage: "",
		agreeTermsAndPrivacy: false,
	});

	// Get today's date in YYYY-MM-DD format for min date
	const getTodayDate = () => {
		const today = new Date();
		return today.toISOString().split("T")[0];
	};

	// Convert date from YYYY-MM-DD to DD/MM/YYYY
	const formatDateForSalesforce = (dateString: string) => {
		if (!dateString) return "";
		const [year, month, day] = dateString.split("-");
		return `${day}/${month}/${year}`;
	};

	const validateForm = (): boolean => {
		const newErrors: Record<string, string> = {};

		// Required fields
		if (!formData.firstName.trim()) {
			newErrors.firstName = "First name is required";
		}
		if (!formData.lastName.trim()) {
			newErrors.lastName = "Last name is required";
		}
		if (!formData.accountNumber.trim()) {
			newErrors.accountNumber =
				"Your felix account mobile number is required";
		}
		if (!formData.contactMethod.trim()) {
			newErrors.contactMethod = "How would you like us to contact you?";
		}

		// Conditional validation based on contact method
		if (formData.contactMethod === "Phone") {
			if (!formData.contactMsisdn.trim()) {
				newErrors.contactMsisdn = "Phone number is required for phone contact";
			} else if (!/^\d{10}$/.test(formData.contactMsisdn.replace(/\s/g, ""))) {
				newErrors.contactMsisdn = "Please enter a valid 10-digit phone number";
			}
		} else if (formData.contactMethod === "Email") {
			if (!formData.notificationEmail.trim()) {
				newErrors.notificationEmail = "Email is required for email contact";
			} else if (
				!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.notificationEmail)
			) {
				newErrors.notificationEmail = "Please enter a valid email address";
			}
		}

		if (!formData.howCanWeAssist.trim()) {
			newErrors.howCanWeAssist = "Please describe how we can assist you";
		}

		// Date validation - ensure dates are not in the past
		const today = new Date();
		today.setHours(0, 0, 0, 0); // Reset time to start of day

		if (formData.callBackTime1) {
			const date1 = new Date(formData.callBackTime1);
			if (date1 < today) {
				newErrors.callBackTime1 =
					"Best date to contact you cannot be in the past";
			}
		}

		if (formData.callBackTime2) {
			const date2 = new Date(formData.callBackTime2);
			if (date2 < today) {
				newErrors.callBackTime2 =
					"Alternative date to contact you cannot be in the past";
			}
		}

		if (!formData.agreeTermsAndPrivacy) {
			newErrors.agreeTermsAndPrivacy =
				"You must agree to the terms and privacy policy";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		const isValid = validateForm();

		if (!isValid) {
			e.preventDefault();
			// Find first error field and scroll to it with offset for better visibility
			const firstErrorKey = Object.keys(errors)[0];
			const errorElement = document.getElementById(firstErrorKey);
			if (errorElement) {
				const yOffset = -100; // Offset to show field clearly
				const y =
					errorElement.getBoundingClientRect().top +
					window.pageYOffset +
					yOffset;
				window.scrollTo({ top: y, behavior: "smooth" });
				// Focus the field for better UX
				errorElement.focus();
			}
			return;
		}

		// Allow the form to submit naturally to Salesforce
		// The form action will handle the submission and Salesforce will redirect to retURL
	};

	const handleInputChange = (
		e: React.ChangeEvent<
			HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
		>,
	) => {
		const { name, value, type } = e.target;
		const checked =
			type === "checkbox" ? (e.target as HTMLInputElement).checked : undefined;

		// Handle multiple select
		if (
			type === "select-multiple" ||
			(e.target as HTMLSelectElement).multiple
		) {
			const selectElement = e.target as HTMLSelectElement;
			const selectedValues = Array.from(selectElement.selectedOptions).map(
				(option) => option.value,
			);

			// Map Salesforce field names to form data property names
			const fieldNameMap: Record<string, string> = {
				"00ND0000006EQOa": "callBackTime1",
				"00ND0000006EQOb": "callBackTime2",
			};

			const formFieldName = fieldNameMap[name] || name;

			setFormData((prev) => ({
				...prev,
				[formFieldName]: selectedValues,
			}));

			// Clear error when user starts typing
			if (errors[formFieldName]) {
				setErrors((prev) => {
					const newErrors = { ...prev };
					delete newErrors[formFieldName];
					return newErrors;
				});
			}
			return;
		}

		// Map Salesforce field names to form data property names
		const fieldNameMap: Record<string, string> = {
			"00ND0000006EQOa": "callBackTime1",
			"00ND0000006EQOb": "callBackTime2",
		};

		const formFieldName = fieldNameMap[name] || name;

		setFormData((prev) => ({
			...prev,
			[formFieldName]: type === "checkbox" ? checked : value,
		}));

		// Clear error when user starts typing
		if (errors[formFieldName]) {
			setErrors((prev) => {
				const newErrors = { ...prev };
				delete newErrors[formFieldName];
				return newErrors;
			});
		}
	};

	return (
		<FormContainer>
			<IntroText>
				Complete this form to request a callback from the dedicated support team
				at a suitable time. They're available from 8:30 am - 4:30pm (Mon-Fri).
				<br />
				<br />
				If our attempts to reach you are unsuccessful on the preferred date,
				please nominate a second date that we can contact you on.
			</IntroText>
			<StyledForm
				action="https://webto.salesforce.com/servlet/servlet.WebToCase?encoding=UTF-8&orgId=00D200000000GC5"
				method="POST"
				onSubmit={handleSubmit}
			>
				{/* Hidden fields */}
				<input type="hidden" name="orgid" value="00D200000000GC5" />
				<input
					type="hidden"
					name="retURL"
					value="https://felixmobile.com.au/domestic-violence/success"
				/>
				<input type="hidden" name="subject" value="Customer Contact Request" />
				<input type="hidden" name="status" value="New" />
				<input type="hidden" name="reason" value="Vulnerable Customer - Web" />
				<input type="hidden" name="recordType" value="012RE000000nuU5" />
				<input
					type="hidden"
					name="00ND0000003Loev"
					value="https://felixmobile.com.au/domestic-violence"
				/>
				<input type="hidden" name="00N20000001RBSZ" value="Felix" />

				<FormField>
					<Label htmlFor="firstName" required>
						First name
					</Label>
					<Input
						type="text"
						id="firstName"
						name="firstName"
						value={formData.firstName}
						onChange={handleInputChange}
						hasError={!!errors.firstName}
						maxLength={255}
					/>
					{errors.firstName && <ErrorMessage>{errors.firstName}</ErrorMessage>}
				</FormField>

				<input
					type="hidden"
					name="00ND0000003M0Dq"
					value={formData.firstName}
				/>

				<FormField>
					<Label htmlFor="lastName" required>
						Last name
					</Label>
					<Input
						type="text"
						id="lastName"
						name="lastName"
						value={formData.lastName}
						onChange={handleInputChange}
						hasError={!!errors.lastName}
						maxLength={255}
					/>
					{errors.lastName && <ErrorMessage>{errors.lastName}</ErrorMessage>}
				</FormField>

				<input type="hidden" name="00ND0000003M0Ds" value={formData.lastName} />

				<FormField>
					<Label htmlFor="accountNumber" required>
						Your felix account mobile number
					</Label>
					<Input
						type="text"
						id="accountNumber"
						name="accountNumber"
						value={formData.accountNumber}
						onChange={handleInputChange}
						hasError={!!errors.accountNumber}
						maxLength={50}
					/>
					{errors.accountNumber && (
						<ErrorMessage>{errors.accountNumber}</ErrorMessage>
					)}
				</FormField>

				<input
					type="hidden"
					name="00ND0000006NN6Z"
					value={formData.accountNumber}
				/>

				<FormField>
					<Label htmlFor="contactMethod" required>
						How would you like us to contact you?
					</Label>
					<Select
						id="contactMethod"
						name="contactMethod"
						value={formData.contactMethod}
						onChange={handleInputChange}
						hasError={!!errors.contactMethod}
					>
						<option value="">Select contact method</option>
						<option value="Phone">Phone</option>
						<option value="Email">Email</option>
					</Select>
					{errors.contactMethod && (
						<ErrorMessage>{errors.contactMethod}</ErrorMessage>
					)}
				</FormField>

				<input
					type="hidden"
					name="00ND0000005yFON"
					value={formData.contactMethod}
				/>

				<FormField>
					<Label htmlFor="contactMsisdn" required>
						Phone Number
					</Label>
					<Input
						type="tel"
						id="contactMsisdn"
						name="contactMsisdn"
						value={formData.contactMsisdn}
						onChange={handleInputChange}
						hasError={!!errors.contactMsisdn}
						maxLength={40}
						placeholder="04XX XXX XXX"
					/>
					{errors.contactMsisdn && (
						<ErrorMessage>{errors.contactMsisdn}</ErrorMessage>
					)}
				</FormField>

				<input
					type="hidden"
					name="00ND0000006Rsbn"
					value={formData.contactMsisdn}
				/>

				<FormField>
					<Label htmlFor="notificationEmail" required>
						Email Address
					</Label>
					<Input
						type="email"
						id="notificationEmail"
						name="notificationEmail"
						value={formData.notificationEmail}
						onChange={handleInputChange}
						hasError={!!errors.notificationEmail}
						maxLength={80}
						placeholder="email@example.com"
					/>
					{errors.notificationEmail && (
						<ErrorMessage>{errors.notificationEmail}</ErrorMessage>
					)}
				</FormField>

				<input
					type="hidden"
					name="00ND0000006Rsbm"
					value={formData.notificationEmail}
				/>

				<FormField>
					<Label htmlFor="callBackTime1">Best date to contact you</Label>
					<Input
						type="date"
						id="callBackTime1"
						name="callBackTime1"
						value={formData.callBackTime1}
						onChange={handleInputChange}
						min={getTodayDate()}
						hasError={!!errors.callBackTime1}
					/>
					{errors.callBackTime1 && (
						<ErrorMessage>{errors.callBackTime1}</ErrorMessage>
					)}
				</FormField>

				<input
					type="hidden"
					name="00ND0000006EQOa"
					value={formatDateForSalesforce(formData.callBackTime1)}
				/>

				<FormField>
					<Label htmlFor="preferredContactTime">Preferred contact time</Label>
					<Select
						id="preferredContactTime"
						name="preferredContactTime"
						value={formData.preferredContactTime}
						onChange={handleInputChange}
						hasError={!!errors.preferredContactTime}
					>
						<option value="">Select preferred time</option>
						<option value="Morning (before 12pm)">Morning (before 12pm)</option>
						<option value="Afternoon (before 4.30pm)">
							Afternoon (before 4.30pm)
						</option>
					</Select>
					{errors.preferredContactTime && (
						<ErrorMessage>{errors.preferredContactTime}</ErrorMessage>
					)}
				</FormField>

				<input
					type="hidden"
					name="00NRE000005UGXq"
					value={formData.preferredContactTime}
				/>

				<FormField>
					<Label htmlFor="callBackTime2">Alternative date to contact you</Label>
					<Input
						type="date"
						id="callBackTime2"
						name="callBackTime2"
						value={formData.callBackTime2}
						onChange={handleInputChange}
						min={getTodayDate()}
						hasError={!!errors.callBackTime2}
					/>
					{errors.callBackTime2 && (
						<ErrorMessage>{errors.callBackTime2}</ErrorMessage>
					)}
				</FormField>

				<input
					type="hidden"
					name="00ND0000006EQOb"
					value={formatDateForSalesforce(formData.callBackTime2)}
				/>

				<FormField>
					<Label htmlFor="howCanWeAssist" required>
						How can we assist you?
					</Label>
					<TextArea
						id="howCanWeAssist"
						name="howCanWeAssist"
						value={formData.howCanWeAssist}
						onChange={handleInputChange}
						hasError={!!errors.howCanWeAssist}
						rows={3}
						placeholder="Please describe how we can help you"
					/>
					{errors.howCanWeAssist && (
						<ErrorMessage>{errors.howCanWeAssist}</ErrorMessage>
					)}
				</FormField>

				<input
					type="hidden"
					name="00NRE000004MoiD"
					value={formData.howCanWeAssist}
				/>

				<FormField>
					<Label htmlFor="safeToLeaveMessage">
						If we can't reach you, is it safe to leave a voicemail?
					</Label>
					<Select
						id="safeToLeaveMessage"
						name="safeToLeaveMessage"
						value={formData.safeToLeaveMessage}
						onChange={handleInputChange}
					>
						<option value="">Select option</option>
						<option value="YES">Yes</option>
						<option value="NO">No</option>
					</Select>
				</FormField>

				<input
					type="hidden"
					name="00NRE000004MolR"
					value={formData.safeToLeaveMessage}
				/>

				<FormField>
					<CheckboxLabel
						htmlFor="agreeTermsAndPrivacy"
						hasError={!!errors.agreeTermsAndPrivacy}
					>
						<CheckboxInput
							type="checkbox"
							id="agreeTermsAndPrivacy"
							name="agreeTermsAndPrivacy"
							checked={formData.agreeTermsAndPrivacy}
							onChange={handleInputChange}
						/>
						<CheckboxBox checked={formData.agreeTermsAndPrivacy} />
						<span>
							By ticking this box I declare that the information provided is
							true and accurate at the time of completion. I also acknowledge
							that the information provided will be used and stored in
							accordance with the Australian Privacy Act and felix mobile's{" "}
							<a
								href="/terms-policies/privacy"
								target="_blank"
								rel="noopener noreferrer"
							>
								Privacy Policy
							</a>
							. *
						</span>
					</CheckboxLabel>
					{errors.agreeTermsAndPrivacy && (
						<ErrorMessage>{errors.agreeTermsAndPrivacy}</ErrorMessage>
					)}
				</FormField>

				<input
					type="hidden"
					name="00ND0000006NN6b"
					value={formData.agreeTermsAndPrivacy ? "TRUE" : "FALSE"}
				/>

				<input
					type="hidden"
					name="00ND0000006NN6c"
					value={formData.agreeTermsAndPrivacy ? "TRUE" : "FALSE"}
				/>

				<SubmitButton type="submit">Submit request</SubmitButton>
			</StyledForm>
		</FormContainer>
	);
};

export default DfsvForm;
